import React from 'react';
import styled from '@benzinga/themetron';
import { offeringToCardProps } from './OfferingCard';
import Image from 'next/image';

import { AltOffering, OfferingTag } from '@benzinga/alts-manager';
import { Icon } from '@benzinga/core-ui';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { SmartLink } from '@benzinga/analytics';

export interface OfferingSingleWideProps {
  offerings: AltOffering[];
}

export const OfferingSingleWide: React.FC<OfferingSingleWideProps> = ({ offerings }) => {
  if (offerings && offerings?.[0]) {
    const mainOffering = offerings[0];
    const cardProps = offeringToCardProps(mainOffering);
    return (
      <OfferingSingleWideBlock className="offering-horizontal flex items-center flex-col  sm:flex-row border rounded-md mb-4">
        {cardProps.primaryImage && (
          <div className="basis-full sm:basis-1/2 w-full">
            <a
              className="image-wrapper"
              data-label="Offering Details"
              href={`/alternative-investments/listing/${cardProps.slug}`}
            >
              <Thumbnail className="card-image">
                <Image
                  alt=""
                  className="object-cover"
                  fill
                  sizes="(max-width: 639px) 100vw, 50vw"
                  src={cardProps.primaryImage}
                />
              </Thumbnail>
            </a>
          </div>
        )}
        <div className="basis-full sm:basis-1/2 w-full ">
          <div className="listing-details-wrap p-3">
            <div className="listing-info-heading-text">
              <a data-label="Offering Details" href={`/alternative-investments/listing/${cardProps.slug}`}>
                <h3 className="listing-title">{cardProps.title}</h3>
              </a>
              {cardProps?.subtitle && <p className="listing-subtitle">{cardProps.subtitle}</p>}
              {cardProps?.platform &&
                (cardProps?.platform?.slug ? (
                  <a href={`/alternative-investments/platform/${cardProps?.platform?.slug}`}>
                    <div className="listing-company">{cardProps?.platform.name}</div>
                  </a>
                ) : (
                  <div className="listing-company">{cardProps?.platform.name}</div>
                ))}
            </div>

            <div className="tags-container mt-3">
              {cardProps?.tags &&
                cardProps?.tags.map((tag: OfferingTag) => (
                  <button className={`offering-tag ${tag.variant ?? 'default'}`} key={tag.label}>
                    {tag.label}
                  </button>
                ))}
            </div>

            <div className="details-bottom mt-3">
              {cardProps?.attributes && cardProps?.attributes[0] && (
                <div className="offering-attr left-attr">
                  <span className="label">{cardProps?.attributes[0].label}</span>
                  <span className="value">{cardProps?.attributes[0].value}</span>
                </div>
              )}

              {cardProps?.attributes && cardProps?.attributes[1] && (
                <div className="offering-attr right-attr">
                  <span className="label">{cardProps?.attributes[1].label}</span>
                  <span className="value">{cardProps?.attributes[1].value}</span>
                </div>
              )}
            </div>

            {mainOffering?.affiliateLink ? (
              <SmartLink
                className="get-started-button rounded"
                href={mainOffering?.affiliateLink}
                label="Offering Get Started"
              >
                Get Offer <Icon icon={faArrowRight} />
              </SmartLink>
            ) : (
              cardProps?.link && (
                <a
                  className="get-details-button rounded"
                  data-label="Offering Details"
                  href={`/alternative-investments/listing/${cardProps?.slug}`}
                >
                  Get Details <Icon icon={faArrowRight} />
                </a>
              )
            )}
          </div>
        </div>
      </OfferingSingleWideBlock>
    );
  } else {
    return <></>;
  }
};

const OfferingSingleWideBlock = styled.div`
  &.offering-horizontal {
    .image-wrapper {
      display: block;
      width: 100%;
      margin-bottom: 0;
    }
    .listing-company {
      font-size: 0.8rem;
      color: ${({ theme }) => theme.colorPalette.gray500};
    }
    .tags-container {
      height: auto;
      .offering-tag {
        font-size: ${({ theme }) => theme.fontSize.sm};
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        padding: 4px 8px;
        background-color: ${({ theme }) => theme.colorPalette.blue50};
        color: ${({ theme }) => theme.colorPalette.blue500};
        display: inline-block;
        border-radius: ${({ theme }) => theme.borderRadius.default};
        height: 30px;
        &.success {
          color: ${({ theme }) => theme.colorPalette.green500};
          border: 1px solid ${({ theme }) => theme.colorPalette.green500};
          background-color: ${({ theme }) => theme.colorPalette.green50};
        }
      }
    }

    .details-bottom {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;

      > div {
        display: flex;
        flex-direction: column;
      }
    }
    .offering-attr {
      .label {
        color: ${({ theme }) => theme.colorPalette.gray500};
        font-size: ${({ theme }) => theme.fontSize.base};
      }

      .value {
        color: ${({ theme }) => theme.colorPalette.gray700};
        font-size: 20px;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
      }
    }
    .get-details-button,
    .get-started-button {
      display: flex;
      width: 100%;
      background-color: #fa8418;
      padding: 10px;
      justify-content: space-between;
      align-items: center;
      color: #ffffff;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }
  }
`;

const Thumbnail = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  height: 100%;
  min-height: 250px;
  width: 100%;
  overflow: hidden;
  background-color: rgb(217, 217, 217);
`;
